import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Building2,
  Star,
  BarChart3,
  Zap, 
  ArrowRight,
  Gamepad2, // New icon for Play
  Swords, // New icon for Competition
} from 'lucide-react';
import Footer from '../components/Footer'; // Assuming path is correct
import { Header } from '../components/Header'; // Assuming path is correct

// Feature Card Component (Assuming it's available)
const FeatureCard = ({ icon, title, description, delay }) => (
  <motion.div
    className="bg-slate-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
  >
    <div className="mb-4">{icon}</div>
    <h3 className="text-xl font-bold mb-2">{title}</h3>
    <p className="text-gray-400">{description}</p>
  </motion.div>
);

// Testimonial Card Component (Assuming it's available)
const TestimonialCard = ({ quote, author, rating, delay }) => (
  <motion.div
    className="bg-slate-900 rounded-xl p-6 shadow-lg"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
  >
    <div className="mb-4 flex">
      {[...Array(rating)].map((_, i) => (
        <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
      ))}
      {[...Array(5 - rating)].map((_, i) => (
        <Star key={i + rating} className="w-5 h-5 text-gray-500" />
      ))}
    </div>
    <p className="text-gray-300 mb-4 italic">"{quote}"</p>
    <p className="text-blue-400 font-medium">{author}</p>
  </motion.div>
);

// Video Section Component (Assuming it's available)
const VideoSection = () => (
  <section id="video" className="py-16 bg-slate-900/50">
    <div className="container mx-auto px-4">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl md:text-4xl font-bold mb-4">See HotelHero in Action!</h2>
        <p className="text-lg text-gray-300 max-w-2xl mx-auto">
          Get a quick glimpse of the strategic gameplay and empire building.
        </p>
      </motion.div>

      <motion.div
        className="relative max-w-4xl mx-auto rounded-xl overflow-hidden shadow-2xl"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="relative pb-[56.25%] h-0"> {/* 16:9 Aspect Ratio */}
          <iframe
            className="absolute top-0 left-0 w-full h-full"
            src="https://www.youtube.com/embed/Ly5LbUw19S4?si=j7vI1VsO5QUeZ69r?rel=0&autoplay=0&mute=1" // Consider autoplay muted or not
            title="HotelHero Gameplay Preview"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </motion.div>
    </div>
  </section>
);


// --- Play Landing Page Component ---

const PlayLandingPage = () => {
  return (
    <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-[100dvh] text-white">
      <div className="container mx-auto px-4 pt-8 pb-16"> {/* Reduced vertical padding */}
        <Header />

        {/* Hero Section - Focused on Action */}
        <section className="text-center pt-16 pb-20">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-block p-4 bg-blue-600/20 rounded-full mb-6"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
            >
              <Gamepad2 className="w-12 h-12 text-blue-400" />
            </motion.div>

            <motion.h1 // Changed to H1 for SEO on this specific landing page
              className="text-4xl md:text-6xl font-bold mb-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Dive In & <span className="text-blue-400">Dominate!</span>
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              You've seen the hype! Now build your hotel empire, outsmart rivals,
              and climb the leaderboards in HotelHero. <span className='font-bold'>It's free to play!</span>
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              {/* Primary CTA: Play Now (Register) */}
              <Link to="/register">
                <motion.button
                  className="w-full sm:w-auto px-8 py-4 bg-orange-500 rounded-lg font-bold text-lg flex items-center justify-center hover:bg-orange-600 transition-colors shadow-lg"
                  whileHover={{ scale: 1.05, y: -2, boxShadow: "0 10px 15px -3px rgba(249, 115, 22, 0.4), 0 4px 6px -2px rgba(249, 115, 22, 0.2)" }}
                  whileTap={{ scale: 0.95 }}
                >
                  Play Now for Free <Zap className="ml-2 h-5 w-5" />
                </motion.button>
              </Link>
              {/* Secondary CTA: Login */}
              <Link to="/login">
                <motion.button
                  className="w-full sm:w-auto px-8 py-4 bg-transparent border border-white/40 rounded-lg font-medium text-lg flex items-center justify-center hover:bg-white/10 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Already a Hero? Login <ArrowRight className="ml-2 h-5 w-5" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </section>

        {/* Quick Features Section - Highlighting fun/core loops */}
        <section id="quick-features" className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Building2 className="w-10 h-10 text-green-400" />}
              title="Build & Customize"
              description="Design your dream hotel from standard rooms to luxury suites and unique facilities."
              delay={0.2}
            />
            <FeatureCard
              icon={<Swords className="w-10 h-10 text-red-400" />}
              title="Outsmart Rivals"
              description="Compete against other players or AI for market share using sharp strategies."
              delay={0.3}
            />
            <FeatureCard
              icon={<BarChart3 className="w-10 h-10 text-blue-400" />}
              title="Manage & Grow"
              description="Hire staff, run marketing, balance finances, and watch your empire expand!"
              delay={0.4}
            />
          </div>
        </section>

        {/* Video Section */}
        {VideoSection()}

        {/* Social Proof / Testimonials */}
        <section id="what-players-say" className="py-16">
           <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Join the Fun!</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Show maybe 2-3 testimonials */}
             <TestimonialCard
              quote="Clicked an ad, now I'm hooked! The competition is intense but fair. Love planning my next move."
              author="SocialMediaPlayer1"
              rating={5}
              delay={0.2}
            />
             <TestimonialCard
              quote="Finally, a hotel tycoon that makes you THINK! It's easy to start but hard to master. Highly recommend."
              author="StrategyFanatic"
              rating={5}
              delay={0.3}
            />
            <TestimonialCard
              quote="Came from TikTok, stayed for the satisfying empire building. Watching my hotel grow is awesome."
              author="TycoonTokker"
              rating={4}
              delay={0.4}
            />
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16">
          <motion.div
            className="max-w-3xl mx-auto bg-gradient-to-r from-blue-900/70 to-blue-800/70 rounded-2xl p-8 md:p-12 shadow-2xl text-center backdrop-blur-sm border border-blue-700/30"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2
              className="text-3xl md:text-4xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Your First 5-Star Hotel Awaits!
            </motion.h2>
            <motion.p
              className="text-lg text-gray-300 mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Stop watching, start building! Click below to register instantly and begin your journey to become a Hotel Hero.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Link to="/register">
                <motion.button
                  className="px-10 py-4 bg-orange-500 rounded-lg font-bold text-xl flex items-center justify-center mx-auto hover:bg-orange-600 transition-colors shadow-lg"
                  whileHover={{ scale: 1.05, y: -2, boxShadow: "0 10px 15px -3px rgba(249, 115, 22, 0.4), 0 4px 6px -2px rgba(249, 115, 22, 0.2)" }}
                  whileTap={{ scale: 0.95 }}
                >
                  Become a Hotel Hero <Zap className="ml-2 h-6 w-6" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default PlayLandingPage;